import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ScreenSearchDesktopOutlinedIcon from '@mui/icons-material/ScreenSearchDesktopOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import {
  LIGHT_GRAY, PRIMARY_COLORS, SECONDARY_COLORS, SURFACE_COLORS, TEXT_GRAY, WHITE,
} from '../../common/colors';

// OCL Online as a diagram: the open-source tools, the content they hold, the
// OCL AI Assistant that works across them (only on OCL Online), and the
// people and systems that use it. Drawn in HTML rather than shipped as an
// image so the labels are translated and set in the site's own type.
// Everything is sized in units of a 1400 × 1000 canvas (`u`), which the
// container-query width scales to the frame, so the whole diagram shrinks
// and grows like the screenshots on the other tool pages.
const u = n => `calc(var(--u) * ${n})`;

const AUDIENCE_ICONS = [DnsOutlinedIcon, GroupsOutlinedIcon, LocalHospitalOutlinedIcon];

const At = ({ x, y, w, h, sx, children }) => (
  <Box sx={{ position: 'absolute', left: u(x), top: u(y), width: w == null ? 'auto' : u(w), height: h == null ? 'auto' : u(h), boxSizing: 'border-box', ...sx }}>
    {children}
  </Box>
);

const Tile = ({ icon: Icon, size, color = PRIMARY_COLORS.main, iconColor = WHITE }) => (
  <Box sx={{ width: u(size), height: u(size), borderRadius: u(size * 0.2), backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon sx={{ color: iconColor, fontSize: u(size * 0.58) }} />
  </Box>
);

const Text = ({ size, weight = 400, color = SURFACE_COLORS.dark, sx, children }) => (
  <Box component="span" sx={{ display: 'block', fontSize: u(size), fontWeight: weight, lineHeight: 1.25, color, ...sx }}>
    {children}
  </Box>
);

const Card = ({ sx, children }) => (
  <Box sx={{ backgroundColor: WHITE, border: `${u(2)} solid ${LIGHT_GRAY}`, borderRadius: u(20), boxSizing: 'border-box', height: '100%', ...sx }}>
    {children}
  </Box>
);

const Chip = ({ children }) => (
  <Box sx={{ backgroundColor: SURFACE_COLORS.main, border: `${u(2)} solid ${LIGHT_GRAY}`, borderRadius: u(999), px: u(16), py: u(5) }}>
    <Text size={21} weight={500}>{children}</Text>
  </Box>
);

// Straight connectors on a 1400 × 1000 overlay that matches the canvas. One
// arrowhead marker per colour: `fill="context-stroke"` isn't safe in Safari.
const Lines = ({ lines, markerId }) => {
  const colors = [...new Set(lines.filter(line => line.arrows).map(line => line.color))];
  const marker = color => `${markerId}-${colors.indexOf(color)}`;
  return (
    <Box component="svg" viewBox="0 0 1400 1000" aria-hidden sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
      <defs>
        {colors.map(color => (
          <marker key={color} id={marker(color)} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
          </marker>
        ))}
      </defs>
      {lines.map(({ x1, y1, x2, y2, color = PRIMARY_COLORS['80'], width = 4, arrows = false, dashed = false }) => (
        <line
          key={`${x1}-${y1}-${x2}-${y2}`}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={color} strokeWidth={width} strokeLinecap="round"
          strokeDasharray={dashed ? '2 12' : undefined}
          markerStart={arrows ? `url(#${marker(color)})` : undefined}
          markerEnd={arrows ? `url(#${marker(color)})` : undefined}
        />
      ))}
    </Box>
  );
};

const Panel = ({ h }) => (
  <At x={40} y={32} w={1320} h={h} sx={{ backgroundColor: PRIMARY_COLORS['95'], border: `${u(2)} solid ${PRIMARY_COLORS['90']}`, borderRadius: u(30) }} />
);

const Header = ({ d }) => (
  <At x={84} y={62} sx={{ display: 'flex', alignItems: 'center', gap: u(18) }}>
    <Tile icon={CloudOutlinedIcon} size={56} />
    <Text size={40} weight={700} sx={{ whiteSpace: 'nowrap' }}>{d.title}</Text>
    <Text size={23} color={TEXT_GRAY} sx={{ ml: u(6), whiteSpace: 'nowrap' }}>{d.subtitle}</Text>
  </At>
);

// The open-source tools sit in their own dashed group, so the OCL AI
// Assistant reads as something OCL Online adds rather than another tool.
const OpenSourceGroup = ({ d, x, y, w, h }) => (
  <At x={x} y={y} w={w} h={h} sx={{ backgroundColor: 'rgba(255,255,255,.5)', border: `${u(2.5)} dashed ${PRIMARY_COLORS['70']}`, borderRadius: u(24), px: u(24), pt: u(16) }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: u(8) }}>
      <CodeOutlinedIcon sx={{ color: PRIMARY_COLORS.main, fontSize: u(28) }} />
      <Text size={21} weight={700} color={PRIMARY_COLORS.main} sx={{ letterSpacing: '.02em' }}>{d.open_source}</Text>
    </Box>
  </At>
);

const AppCard = ({ icon, tool, x, y, w, h }) => (
  <At x={x} y={y} w={w} h={h}>
    <Card sx={{ display: 'flex', alignItems: 'center', gap: u(18), px: u(22) }}>
      <Tile icon={icon} size={52} />
      <Box>
        <Text size={29} weight={700}>{tool.name}</Text>
        <Text size={21} color={TEXT_GRAY} sx={{ mt: u(4) }}>{tool.text}</Text>
      </Box>
    </Card>
  </At>
);

const ContentGroup = ({ group }) => (
  <>
    <Text size={21} weight={700} color={SECONDARY_COLORS['30']}>{group.label}</Text>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: u(10) }}>
      {group.items.map(item => <Chip key={item}>{item}</Chip>)}
    </Box>
  </>
);

// The Terminology Service holds the content, so the two kinds of content
// live inside its card, one row each. The label column takes the width of the
// longest label in the current language, and the card leaves room for the
// chips to wrap once (Spanish runs long); the rows centre in whatever is left.
const TerminologyServiceCard = ({ d, x, y, w, h }) => (
  <At x={x} y={y} w={w} h={h}>
    <Card sx={{ px: u(22), py: u(20), display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: u(18) }}>
        <Tile icon={ApiOutlinedIcon} size={52} />
        <Box>
          <Text size={29} weight={700}>{d.tools.terminology_service.name}</Text>
          <Text size={21} color={TEXT_GRAY} sx={{ mt: u(4) }}>{d.tools.terminology_service.text}</Text>
        </Box>
      </Box>
      <Box sx={{ borderTop: `${u(2)} solid ${LIGHT_GRAY}`, mt: u(16), pt: u(16), flex: 1, display: 'grid', gridTemplateColumns: 'max-content 1fr', alignContent: 'center', alignItems: 'center', columnGap: u(20), rowGap: u(12) }}>
        <ContentGroup group={d.content.reference} />
        <ContentGroup group={d.content.community} />
      </Box>
    </Card>
  </At>
);

// Indigo rather than white, with its own badge: a service of OCL Online that
// works across the tools, not another open-source tool.
const AiAssistant = ({ d, x, y, w, h }) => (
  <At x={x} y={y} w={w} h={h} sx={{
    backgroundColor: PRIMARY_COLORS.main, borderRadius: u(24), color: WHITE, p: u(28),
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: u(18),
    boxShadow: `0 ${u(10)} ${u(28)} ${u(-12)} rgba(72,54,255,.55)`,
  }}>
    <Tile icon={AutoAwesomeOutlinedIcon} size={56} color={WHITE} iconColor={PRIMARY_COLORS.main} />
    <Box>
      <Text size={32} weight={700} color={WHITE}>{d.ai_assistant.name}</Text>
      <Text size={22} color="rgba(255,255,255,.88)" sx={{ mt: u(6) }}>{d.ai_assistant.text}</Text>
    </Box>
    <Box sx={{ mt: u(10), border: `${u(2)} solid rgba(255,255,255,.6)`, borderRadius: u(24), px: u(14), py: u(6) }}>
      <Text size={18} weight={700} color={WHITE}>{d.ai_assistant.badge}</Text>
    </Box>
  </At>
);

const Audiences = ({ d, centers, top, width = 320 }) => d.audiences.map((label, i) => {
  const Icon = AUDIENCE_ICONS[i];
  return (
    <At key={label} x={centers[i] - width / 2} y={top} w={width} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Icon sx={{ color: SECONDARY_COLORS['30'], fontSize: u(60) }} />
      <Text size={24} weight={700} sx={{ mt: u(8) }}>{label}</Text>
    </At>
  );
});

// The open-source tools on the left, the OCL AI Assistant beside them, and
// the people and systems that use OCL Online underneath.
const Layout = ({ d, markerId }) => {
  const centers = [224, 519, 814];
  return (
    <>
      <Panel h={682} />
      <Header d={d} />
      <OpenSourceGroup d={d} x={84} y={146} w={870} h={536} />
      <AppCard icon={ScreenSearchDesktopOutlinedIcon} tool={d.tools.termbrowser} x={108} y={200} w={399} h={112} />
      <AppCard icon={AccountTreeOutlinedIcon} tool={d.tools.mapper} x={531} y={200} w={399} h={112} />
      <TerminologyServiceCard d={d} x={108} y={344} w={822} h={312} />
      <AiAssistant d={d} x={1010} y={146} w={306} h={536} />
      <Audiences d={d} centers={centers} top={816} width={286} />
      <Lines
        markerId={markerId}
        lines={[
          { x1: 307, y1: 312, x2: 307, y2: 344 },
          { x1: 730, y1: 312, x2: 730, y2: 344 },
          { x1: 962, y1: 256, x2: 1002, y2: 256, color: PRIMARY_COLORS.main, width: 5, arrows: true, dashed: true },
          { x1: 962, y1: 500, x2: 1002, y2: 500, color: PRIMARY_COLORS.main, width: 5, arrows: true, dashed: true },
          ...centers.map(x => ({ x1: x, y1: 728, x2: x, y2: 800, color: SECONDARY_COLORS['70'], width: 7, arrows: true })),
        ]}
      />
    </>
  );
};

const OclOnlineDiagram = () => {
  const { t } = useTranslation();
  const markerId = `ocl-online-arrow-${React.useId().replace(/:/g, '')}`;
  const d = t('ocl_online_diagram', { returnObjects: true });
  return (
    <Box sx={{ containerType: 'inline-size', width: '100%' }}>
      <Box
        role="img"
        aria-label={d.aria_label}
        sx={{ '--u': 'calc(100cqw / 1400)', position: 'relative', width: '100%', aspectRatio: '1.4 / 1', overflow: 'hidden', backgroundColor: WHITE }}>
        <Layout d={d} markerId={markerId} />
      </Box>
    </Box>
  );
};

export default OclOnlineDiagram;
